class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class TgdCartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      this.updateCode();
    });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-discount]');
      if (button) this.updateCode(button.dataset.removeDiscount);
    });
    this.querySelector('input').addEventListener('input', () => {
      this.querySelector('input').removeAttribute('aria-invalid');
      this.querySelector('[role="status"]').hidden = true;
    });
  }

  getCodes(cart) {
    const codes = (cart.discount_codes || []).filter((item) => item.applicable !== false).map((item) => item.code);
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
    ];
    applications.forEach((application) => {
      if (application?.type === 'discount_code') codes.push(application.title);
    });
    return [...new Map(codes.filter(Boolean).map((code) => [code.toLowerCase(), code])).values()];
  }

  showMessage(message, error = false) {
    const status = this.querySelector('[role="status"]');
    status.textContent = message;
    status.dataset.error = String(error);
    status.hidden = false;
    if (error) this.querySelector('input').setAttribute('aria-invalid', 'true');
  }

  async request(codes) {
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error('Cart discount request failed');
    return response.json();
  }

  renderCart(cart, codes, message, error = false) {
    const drawer = this.closest('cart-drawer');
    if (cart.sections?.['cart-drawer'] && cart.sections?.['cart-icon-bubble']) drawer.renderContents(cart);
    const box = drawer.querySelector('tgd-cart-discount');
    if (!box) return;
    const list = box.querySelector('[data-discount-codes]');
    list.replaceChildren();
    codes.forEach((code) => {
      const row = document.createElement('li');
      const title = document.createElement('span');
      title.textContent = code;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removeDiscount = code;
      remove.setAttribute('aria-label', box.dataset.removeLabel + ': ' + code);
      remove.textContent = '×';
      row.append(title, remove);
      list.append(row);
    });
    box.showMessage(message, error);
    box.querySelector('input').focus({ preventScroll: true });
  }

  async updateCode(removeCode) {
    if (this.busy) return;
    const input = this.querySelector('input');
    const code = input.value.trim();
    if (!removeCode && !code) {
      this.showMessage(this.dataset.empty, true);
      input.focus();
      return;
    }
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    const controls = [...this.querySelectorAll('button, input')];
    controls.forEach((control) => { control.disabled = true; });
    const checkout = document.getElementById('CartDrawer-Checkout');
    const checkoutDisabled = checkout?.disabled;
    if (checkout) checkout.disabled = true;
    this.querySelector('[role="status"]').hidden = true;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart read failed');
      const before = await response.json();
      const previousCodes = this.getCodes(before);
      const requestedCodes = removeCode
        ? previousCodes.filter((value) => value.toLowerCase() !== removeCode.toLowerCase())
        : [...new Set([...previousCodes, code])];
      const cart = await this.request(requestedCodes);
      const confirmedCodes = this.getCodes(cart);
      if (!removeCode && !confirmedCodes.some((value) => value.toLowerCase() === code.toLowerCase())) {
        const restored = await this.request(previousCodes);
        this.renderCart(restored, this.getCodes(restored), this.dataset.invalid, true);
        return;
      }
      if (removeCode && confirmedCodes.some((value) => value.toLowerCase() === removeCode.toLowerCase())) {
        throw new Error('Discount was not removed');
      }
      this.renderCart(cart, confirmedCodes, removeCode ? this.dataset.removed : this.dataset.success);
    } catch (error) {
      this.showMessage(this.dataset.error, true);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      controls.forEach((control) => { control.disabled = false; });
      if (checkout?.isConnected) checkout.disabled = checkoutDisabled;
    }
  }
}

customElements.define('tgd-cart-discount', TgdCartDiscount);

